/**
 * dissolve.js - Energy Pulse Dissolve-Effekt
 * Energie-Impulse fließen durch den Donut wie Blut/Strom
 * Bei Linksklick ausgelöst
 */
import * as THREE from "three";
import { settings } from "./settings.js";

// Shader importieren
import vertexShader from "../shaders/dissolve.vert";
import fragmentShader from "../shaders/dissolve.frag";

// State
let dissolveUniforms = null;
let originalMaterial = null;
let dissolveMaterial = null;
let isDissolving = false;
let dissolveDirection = 1; // 1 = auflösen, -1 = wieder erscheinen
let dissolveProgress = 0;

// Konfiguration
const DISSOLVE_SPEED = 2.5; // Sekunden für komplette Auflösung (langsamer für den Effekt)
const PULSE_COLOR = "#000"; // Cyan für elektrische Impulse
const GLOW_COLOR = "#3D2314"; // Orange für den finalen Glow
const PULSE_SPEED = 2.0; // Geschwindigkeit der fließenden Impulse
const PULSE_INTENSITY = 15.5; // Stärke der Impulse
const VEIN_DENSITY = 25.0; // Dichte der Adern

/**
 * Initialisiert den Dissolve-Effekt für ein Mesh
 */
export function initDissolve(mesh) {
  if (!mesh || !mesh.material) return;

  // Original-Material speichern
  originalMaterial = mesh.material;

  // Dissolve-Material erstellen mit neuen Uniforms
  dissolveUniforms = {
    uTime: { value: 0 },
    uDissolveProgress: { value: 0 },
    uBaseColor: { value: originalMaterial.color.clone() },
    uPulseColor: { value: new THREE.Color(PULSE_COLOR) },
    uGlowColor: { value: new THREE.Color(GLOW_COLOR) },
    uPulseSpeed: { value: PULSE_SPEED },
    uPulseIntensity: { value: PULSE_INTENSITY },
    uVeinDensity: { value: VEIN_DENSITY },
  };

  dissolveMaterial = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: dissolveUniforms,
    side: THREE.DoubleSide,
    transparent: true,
  });

  return dissolveMaterial;
}

/**
 * Startet den Dissolve-Effekt
 */
export function triggerDissolve(mesh) {
  if (!mesh || !dissolveMaterial) {
    // Falls noch nicht initialisiert, jetzt machen
    initDissolve(mesh);
  }

  if (!dissolveMaterial) return;

  // Wenn bereits am Auflösen oder aufgelöst, Richtung umkehren
  if (isDissolving) {
    dissolveDirection *= -1;
  } else {
    // Neue Animation starten
    isDissolving = true;

    // Wenn komplett aufgelöst, wieder erscheinen lassen
    if (dissolveProgress >= 1) {
      dissolveDirection = -1;
    } else {
      dissolveDirection = 1;
    }

    // Material wechseln
    mesh.material = dissolveMaterial;

    // Basis-Farbe vom Original-Material übernehmen
    if (originalMaterial && originalMaterial.color) {
      dissolveUniforms.uBaseColor.value.copy(originalMaterial.color);
    }
  }
}

/**
 * Aktualisiert den Dissolve-Effekt
 */
export function updateDissolve(time, deltaTime, mesh) {
  if (!dissolveUniforms) return;

  dissolveUniforms.uTime.value = time;

  if (isDissolving) {
    // Progress aktualisieren
    const speed = 1.0 / DISSOLVE_SPEED;
    dissolveProgress += dissolveDirection * deltaTime * speed;

    // Grenzen prüfen
    if (dissolveProgress >= 1) {
      dissolveProgress = 1;
      isDissolving = false;
      // Bleibt aufgelöst bis nächster Klick
    } else if (dissolveProgress <= 0) {
      dissolveProgress = 0;
      isDissolving = false;
      // Wieder Original-Material setzen
      if (mesh && originalMaterial) {
        mesh.material = originalMaterial;
      }
    }

    dissolveUniforms.uDissolveProgress.value = dissolveProgress;
  }
}

/**
 * Setzt den Dissolve-Progress direkt (für PostMessage)
 */
export function setDissolveProgress(progress, mesh) {
  if (!dissolveUniforms) {
    initDissolve(mesh);
  }

  if (!dissolveUniforms || !mesh) return;

  dissolveProgress = Math.max(0, Math.min(1, progress));
  dissolveUniforms.uDissolveProgress.value = dissolveProgress;

  // Material wechseln wenn nötig
  if (dissolveProgress > 0 && mesh.material !== dissolveMaterial) {
    mesh.material = dissolveMaterial;
  } else if (dissolveProgress === 0 && originalMaterial) {
    mesh.material = originalMaterial;
  }
}

/**
 * Setzt die Puls-Farbe (Energie/Strom)
 */
export function setDissolvePulseColor(color) {
  if (dissolveUniforms) {
    dissolveUniforms.uPulseColor.value = new THREE.Color(color);
  }
}

/**
 * Setzt die Glow-Farbe (beim Auflösen)
 */
export function setDissolveGlowColor(color) {
  if (dissolveUniforms) {
    dissolveUniforms.uGlowColor.value = new THREE.Color(color);
  }
}

/**
 * Setzt die Puls-Geschwindigkeit
 */
export function setDissolvePulseSpeed(speed) {
  if (dissolveUniforms) {
    dissolveUniforms.uPulseSpeed.value = speed;
  }
}

/**
 * Setzt die Puls-Intensität
 */
export function setDissolvePulseIntensity(intensity) {
  if (dissolveUniforms) {
    dissolveUniforms.uPulseIntensity.value = intensity;
  }
}

/**
 * Setzt die Adern-Dichte
 */
export function setDissolveVeinDensity(density) {
  if (dissolveUniforms) {
    dissolveUniforms.uVeinDensity.value = density;
  }
}

// Legacy-Funktionen für Abwärtskompatibilität
export function setDissolveEdgeColor(color) {
  setDissolveGlowColor(color);
}

export function setDissolveEdgeWidth(width) {
  // Nicht mehr verwendet, ignorieren
}

export function setDissolveNoiseScale(scale) {
  setDissolveVeinDensity(scale);
}

/**
 * Gibt den aktuellen Dissolve-Status zurück
 */
export function getDissolveState() {
  return {
    isDissolving,
    progress: dissolveProgress,
    direction: dissolveDirection,
  };
}

/**
 * Aktualisiert die Basis-Farbe (wenn Donut-Farbe geändert wird)
 */
export function syncDissolveColor(mesh) {
  if (mesh && mesh.material && mesh.material !== dissolveMaterial) {
    originalMaterial = mesh.material;
    if (dissolveUniforms && originalMaterial.color) {
      dissolveUniforms.uBaseColor.value.copy(originalMaterial.color);
    }
  }
}
